import React, { useEffect, useMemo, useState } from "react";
import { compose, graphql } from "react-apollo";
import TableView, {
  ActionCell,
  buyerTwoIconCellObject,
  DetailLinkCell,
  EmailCell,
  includes,
  ListCell,
  PhoneCell,
  stringComparator,
  TextHeader,
} from "../../components/new-table";
import FadeIn from "../../components/fade-in";
import { phoneDisplay } from "../../utils/helpers";
import routes_constants from "../layout/routes";
import { GET_PROSPECTS_FOR_ALL_PROSPECTS } from "../../graphql/queries";
import { FaStar } from "react-icons/fa";

/** @module GlobalProspectsTableModule */
/**
 * This component is a table used to display prospects data based on the global search query. It contains only the top 5 results and further specific query needs to be made
 * to get the appropriate data. It also has a basic hover action on Name column which redirect to the prospects details.
 *
 * @param {Object} props this contains the data from the graphql query and the function to close the drawer
 * @param {String} props.searchTerm it contains the search query from the GlobalSearchDetails component
 * @param {Function} props.handleDrawerClose function to close the drawer
 * @param {Object} props.data it contains array of prospects to be shown on table
 * @returns {JSX.Element} It returns jsx containing the table title and the table itself
 */
const GlobalProspectsTable = ({ handleDrawerClose, data, setMergeData }) => {
  const [loading, setLoading] = useState(true);
  const [render, setRender] = useState(false);
  const [Prospects, setProspects] = useState({});

  /**
   * Either set the data in state or set the loading to inform the table what to render.
   */
  useEffect(() => {
    if (!data.loading && data.getProspects && data.getProspects.Prospects) {
      setLoading(false);
      setRender(true);
      setProspects(data.getProspects.Prospects);
    }
    if (data.loading) {
      setLoading(data.loading);
    }
  }, [data]);

  /**
   * This function adds customers to the merge customer list on clicking the merge button. If the customer selected is already
   * in the merge customers list then the customer is not added a second time.
   * @param {Object} prospect prospect data which is to be added into the merged customer list
   */
  const updateMergeList = (prospect) => {
    setMergeData(prevState => {
      const data = prevState.type === "realtor" ? [prospect]
        : prevState.data?.find(customer => customer.customer_id === prospect.customer_id)
          ? prevState.data
          : [...prevState.data, prospect]
      return {
        data,
        type: "customer"
      }
    })
  }

  /**
  * Columns of the table containing a column to show an icon in case there is a second buyer, name, community, phone and email.
  * @constant
  * @memberof module:GlobalProspectsTableModule
  */
  const columns = useMemo(() => [
    {
      id: "0",
      header: "",
      accessor: "",
      component: TextHeader,
      cell: buyerTwoIconCellObject,
    },
    {
      id: "1",
      header: "Name",
      accessor: "first_name_1",
      component: TextHeader,
      sortable: false,
      cell: {
        component: DetailLinkCell,
        path: (field, accessor) => routes_constants.PROSPECT_DETAIL,
        mapper: (field, accessor) =>
          field[accessor] + " " + field["last_name_1"],
        action: handleDrawerClose,
      },
    },
    {
      id: "2",
      header: "Community",
      accessor: "all_communities",
      component: TextHeader,
      sortable: false,
      csvMapper: (field, accessor) =>
        field[accessor] ? field[accessor].map(({ name }) => name) : "",
      cell: {
        component: ListCell,
      },
    },
    {
      id: "3",
      header: "Phone",
      accessor: "cell_phone_1",
      component: TextHeader,
      sortable: false,
      filter: includes,
      csvMapper: (field, accessor) => phoneDisplay(field[accessor]) || "",
      cell: { component: PhoneCell },
    },
    {
      id: "4",
      header: "Email",
      accessor: "primary_email_1",
      component: TextHeader,
      sortable: false,
      comparator: stringComparator,
      cell: {
        component: EmailCell,
        check: "primary_email_1_status",
      },
    },
  ]);

  /**
   * Array of objects containing different actions which can be performed on row hover.
   * @constant
   * @memberof module:GlobalProspectsTable
   */
  const hoverActions = React.useMemo(
    () => [
      {
        textValue: 'Merge',
        component: ActionCell,
        action: (event) => {
          updateMergeList(event);
        },
      }
    ],
    []
  );

  return (
    <div>
      <div className="d-flex header-table">
        <FaStar className="header-icon" />
        <div className="d-flex align-self-center header-text">
          Prospects {!Prospects.length && "(No records Found)"}
        </div>
      </div>
      {Prospects.length ? (
        <div className="results-table-search">
          <FadeIn show={render}>
            <TableView
              columns={columns}
              data={Prospects}
              loading={loading}
              currentPage={0}
              hoverActions={hoverActions}
              isGlobalSearch={true}
              controlled={true}
            />
          </FadeIn>
        </div>
      ) : null}
    </div>
  );
};

export default compose(
  graphql(GET_PROSPECTS_FOR_ALL_PROSPECTS, {
    options: ({ searchTerm }) => {
      return {
        variables: {
          pageNum: 0,
          limit: 5,
          sort: null,
          search: searchTerm || "",
        },
        fetchPolicy: "cache-and-network",
      };
    },
  })
)(GlobalProspectsTable);
